// src/ConsumerApp/Screens/Account/views/WalletView.js

import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { theme } from '../../../../assets/theme/theme';

import WalletIcon from '../../../../assets/icons/Wallet.svg';
import ClockCounterClockwise from '../../../../assets/icons/ClockCounterClockwise.svg';
import ArrowLeft from '../../../../assets/icons/ArrowLeft.svg';

import { useGetWalletBalance, useGetWalletHistory } from '../../../api/wallet/wallet.hooks';

export default function WalletView() {
  const navigate = useNavigate();

  const customer = useMemo(() => {
    try {
      const stored = sessionStorage.getItem('userData') || localStorage.getItem('userData');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  }, []);

  const customerId = customer?._id || customer?.id;
  const [showHistory, setShowHistory] = useState(true);

  const { data: balanceData, isLoading: isLoadingBalance } = useGetWalletBalance(customerId);
  const { data: historyResponse, isLoading: isLoadingHistory } = useGetWalletHistory(customerId, 1, 30);

  const availableBalance = Number(balanceData?.available_wallet_balance ?? 0);

  const mappedHistory = useMemo(() => {
    const rawHistory = historyResponse?.wallet_history || historyResponse?.data?.wallet_history || [];

    return rawHistory.map((item) => {
      const isDebit = item.transaction_type?.toLowerCase() === 'debit';
      const rawAmount = Number(item.amount) || 0;

      return {
        id: item.transaction_id || item._id,
        title: item.title || (item.order_number ? `Order #${item.order_number}` : 'Transaction'),
        amount: `${isDebit ? '-' : '+'}₹${rawAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`,
        isDebit,
        date: item.created_at
          ? new Date(item.created_at).toLocaleDateString('en-IN', {
              day: '2-digit',
              month: 'short',
              year: 'numeric',
            })
          : '',
      };
    });
  }, [historyResponse]);

  return (
    <div>
      <style>{`
        .bb-sub-circle-back-btn {
          width: 40px;
          height: 40px;
          border-radius: 999px;
          border: 1px solid #E8E0D0;
          background-color: #FFFFFF;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          flex-shrink: 0;
          padding: 0;
          margin-right: 12px;
          outline: none;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.03);
          transition: transform 0.12s ease, background-color 0.12s ease;
          box-sizing: border-box;
        }
        .bb-sub-circle-back-btn:active {
          transform: scale(0.92);
          background-color: #F8FAFC;
        }
        .bb-sub-circle-back-btn img {
          width: 20px;
          height: 20px;
          display: block;
        }
        .bb-sub-mobile-header {
          display: none;
        }
        .bb-sub-desktop-header {
          display: flex;
        }
        @media (max-width: 900px) {
          .bb-sub-desktop-header {
            display: none !important;
          }
          .bb-sub-mobile-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            width: 100%;
            height: 56px;
            padding: 0 16px;
            background-color: #FFFFFF;
            border-bottom: 1px solid #F1F5F9;
            box-sizing: border-box;
            position: sticky;
            top: 0;
            z-index: 1000;
          }
          .bb-sub-mobile-content {
            padding: 14px 16px 36px 16px !important;
          }
        }
      `}</style>

      {/* Mobile Edge-to-Edge Bar */}
      <div className="bb-sub-mobile-header">
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <button
            type="button"
            className="bb-sub-circle-back-btn"
            onClick={() => navigate('/consumer/account', { replace: true })}
            aria-label="Back to Account"
          >
            <img src={ArrowLeft} alt="Back" />
          </button>
          <h2 style={{
            fontFamily: theme.typography.headingLG.fontFamily,
            fontSize: 18,
            fontWeight: '800',
            color: theme.colors.charcoal,
            margin: 0,
          }}>
            Wallet
          </h2>
        </div>
      </div>

      <div style={styles.container} className="bb-sub-mobile-content">
        {/* Desktop Header */}
        <div style={styles.pageHeader} className="bb-sub-desktop-header">
          <div>
            <h2 style={styles.headerTitle}>Wallet</h2>
            <span style={styles.headerSubtitle}>
              Track your available credit, refunds, and transaction statement
            </span>
          </div>
        </div>

        {/* Wallet Balance Card */}
        <div style={styles.walletCard}>
          <div style={styles.walletCardTop}>
            <div style={styles.titleContainer}>
              <div style={styles.walletIconWrap}>
                <img
                  src={WalletIcon}
                  alt="wallet"
                  style={{
                    width: 16,
                    height: 16,
                    filter: 'brightness(0) invert(1)',
                  }}
                />
              </div>
              <span style={styles.walletTitle}>Available Balance</span>
            </div>

            <button
              type="button"
              style={styles.toggleBtn}
              onClick={() => setShowHistory((prev) => !prev)}
            >
              <img
                src={ClockCounterClockwise}
                alt="history"
                style={{
                  width: 12,
                  height: 12,
                  filter: 'brightness(0) invert(1)',
                }}
              />
              <span>{showHistory ? 'Hide Statement' : 'Show Statement'}</span>
            </button>
          </div>

          <div style={styles.balanceRow}>
            {isLoadingBalance ? (
              <span style={styles.balanceLoading}>Loading balance...</span>
            ) : (
              <span style={styles.balanceValue}>
                ₹{availableBalance.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
              </span>
            )}
          </div>

          <p style={styles.walletSubText}>
            Direct procurement balance. Automatically applied against checkout orders.
          </p>
        </div>

        {/* Transaction History */}
        {showHistory && (
          <div style={styles.historyCard}>
            <div style={styles.historyHeader}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <img
                  src={ClockCounterClockwise}
                  alt="statement"
                  style={{ width: 16, height: 16, opacity: 0.7 }}
                />
                <h3 style={styles.historySectionTitle}>Transaction History</h3>
              </div>
              <span style={styles.historyCountBadge}>{mappedHistory.length} entries</span>
            </div>

            {isLoadingHistory ? (
              <div style={styles.loadingBox}>Fetching statement history...</div>
            ) : mappedHistory.length === 0 ? (
              <div style={styles.emptyBox}>
                <div style={styles.emptyIconCircle}>
                  <span style={{ fontSize: 24 }}>💸</span>
                </div>
                <h4 style={styles.emptyTitle}>No transactions yet</h4>
                <p style={styles.emptySubtitle}>
                  Your wallet activity, including refunds and purchases, will be listed here once you start transacting.
                </p>
              </div>
            ) : (
              <div style={styles.transactionList}>
                {mappedHistory.map((item, index) => {
                  const isLast = index === mappedHistory.length - 1;

                  return (
                    <div
                      key={item.id || index}
                      style={{
                        ...styles.itemRow,
                        borderBottom: isLast ? 'none' : '1px solid #F1F5F9',
                      }}
                    >
                      <div style={styles.itemLeft}>
                        <span style={styles.itemTitle}>{item.title}</span>
                        <span style={styles.itemDate}>{item.date}</span>
                      </div>

                      <div style={styles.itemRight}>
                        <span
                          style={{
                            ...styles.itemAmount,
                            color: item.isDebit ? theme.colors.charcoal : '#16A34A',
                          }}
                        >
                          {item.amount}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
    width: '100%',
  },
  pageHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    borderBottom: '1px solid #E2E8F0',
    paddingBottom: 16,
  },
  headerTitle: {
    ...theme.typography.headingLG,
    margin: 0,
    fontSize: 22,
    fontWeight: '800',
    color: theme.colors.charcoal,
  },
  headerSubtitle: {
    ...theme.typography.bodySM,
    fontSize: 13,
    color: theme.colors.concrete,
    marginTop: 4,
    display: 'block',
  },
  walletCard: {
    background: 'linear-gradient(135deg, #1E293B 0%, #0F172A 100%)',
    borderRadius: 16,
    padding: '24px 28px',
    color: '#FFFFFF',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    minHeight: 140,
    boxShadow: '0 8px 24px rgba(15, 23, 42, 0.12)',
    boxSizing: 'border-box',
    width: '100%',
  },
  walletCardTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  titleContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  walletIconWrap: {
    width: 28,
    height: 28,
    borderRadius: 8,
    backgroundColor: 'rgba(255, 255, 255, 0.18)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  walletTitle: {
    fontFamily: theme.typography.bodyMD.fontFamily,
    fontSize: 14,
    fontWeight: '600',
    color: '#E2E8F0',
  },
  toggleBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    backgroundColor: 'rgba(255, 255, 255, 0.16)',
    border: 'none',
    borderRadius: 999,
    padding: '4px 12px',
    color: '#FFFFFF',
    fontFamily: theme.typography.bodySM.fontFamily,
    fontSize: 11.5,
    fontWeight: '600',
    cursor: 'pointer',
  },
  balanceRow: {
    margin: '14px 0 8px 0',
  },
  balanceValue: {
    fontFamily: theme.typography.headingLG.fontFamily,
    fontSize: 34,
    fontWeight: '800',
    letterSpacing: '-0.5px',
  },
  balanceLoading: {
    fontFamily: theme.typography.bodyMD.fontFamily,
    fontSize: 16,
    opacity: 0.8,
  },
  walletSubText: {
    margin: 0,
    fontFamily: theme.typography.bodySM.fontFamily,
    fontSize: 12,
    color: '#94A3B8',
    lineHeight: '16px',
  },
  historyCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 14,
    border: '1px solid #CBD5E1',
    padding: '20px 24px',
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
  },
  historyHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: '1px solid #F1F5F9',
    paddingBottom: 12,
  },
  historySectionTitle: {
    ...theme.typography.headingMD,
    margin: 0,
    fontSize: 15.5,
    fontWeight: '700',
    color: theme.colors.charcoal,
  },
  historyCountBadge: {
    fontFamily: theme.typography.bodySM.fontFamily,
    fontSize: 11.5,
    color: theme.colors.concrete,
    backgroundColor: '#F8FAFC',
    border: '1px solid #E2E8F0',
    padding: '2px 8px',
    borderRadius: 6,
  },
  transactionList: {
    display: 'flex',
    flexDirection: 'column',
  },
  itemRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 0',
  },
  itemLeft: {
    display: 'flex',
    flexDirection: 'column',
    gap: 3,
  },
  itemTitle: {
    fontFamily: theme.typography.bodyMD.fontFamily,
    fontSize: 13.5,
    fontWeight: '600',
    color: theme.colors.charcoal,
  },
  itemDate: {
    fontFamily: theme.typography.bodySM.fontFamily,
    fontSize: 11.5,
    color: theme.colors.concrete,
  },
  itemRight: {
    display: 'flex',
    alignItems: 'center',
  },
  itemAmount: {
    fontFamily: theme.typography.bodyMD.fontFamily,
    fontSize: 14,
    fontWeight: '700',
  },
  loadingBox: {
    padding: '36px 0',
    textAlign: 'center',
    fontSize: 13,
    color: theme.colors.concrete,
    fontFamily: theme.typography.bodyMD.fontFamily,
  },
  emptyBox: {
    padding: '36px 16px',
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  emptyIconCircle: {
    width: 48,
    height: 48,
    borderRadius: '50%',
    backgroundColor: '#F8FAFC',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 8,
  },
  emptyTitle: {
    ...theme.typography.headingMD,
    margin: 0,
    fontSize: 14.5,
    fontWeight: '700',
    color: theme.colors.charcoal,
  },
  emptySubtitle: {
    ...theme.typography.bodySM,
    margin: '4px 0 0 0',
    fontSize: 12,
    color: theme.colors.concrete,
  },
};